import {CanActivateFn, Router} from '@angular/router';
import {inject} from '@angular/core';
import {LocalStorageService} from '@services/local-storage.service';
import {SidebarService} from '@services/sidebar.service';
import {of, switchMap, timer} from 'rxjs';
import {cache} from '@common/cache-provider';
import {QuickbooksService} from '../../pages/accounting/quickbooks.service';
import {ZohobooksService} from '../../pages/accounting/zohobooks.service';

/**
 * Whether selected client is authorized in accounting platform
 * @param route
 * @param state
 * @param connection whether only a connection needed
 */
export const clientSelectedGuard: CanActivateFn = (
  route, state, connection?: boolean) => {
  const router = inject(Router);
  const {accountingPlatform} = route.data;
  const fail = () => router.parseUrl(`/${accountingPlatform}/initial?ref=${state.url}&connection=${connection || false}`);

  const localStorageService = inject(LocalStorageService);
  const selectedClientUsername = localStorageService.getSelectedClientUsername();

  if (accountingPlatform === 'manual') {
    return !!selectedClientUsername || fail();
  }

  const clientsCache = cache.clients[accountingPlatform];

  if (!selectedClientUsername && !clientsCache) {
    // no selected client, no accounting data
    return fail();
  }

  if (!selectedClientUsername && clientsCache?.length) {
    const sbs = inject(SidebarService);
    const client = clientsCache[0].username;
    sbs.pickClient(client);
    return timer(100).pipe(switchMap(() => of(true)));
  }

  if (clientsCache) {
    return !!clientsCache.find(item => item.username === selectedClientUsername) || fail();
  }

  return fail();
};

/**
 * Is there any client with established connection
 *  Function checks cache for such client
 *  If nothing found in cache will perform check with clientSelectedGuard
 * @param route
 * @param state
 */
export const customerHasConnection: CanActivateFn = (route, state) => {
  const {accountingPlatform} = route.data;

  if (accountingPlatform === 'manual') {
    const localStorageService = inject(LocalStorageService);
    // @ts-ignore
    return !!localStorageService.getSelectedClientUsername() || clientSelectedGuard(route, state, true);
  }

  const serviceByPlatform = {
    'quickbooks': QuickbooksService,
    'zohobooks': ZohobooksService,
  };
  // check for connections cache in platform
  // @ts-ignore
  const {connectionsCache} = inject(serviceByPlatform[accountingPlatform]);
  if (connectionsCache) {
    for (const username in connectionsCache) {
      // at least one authorized user
      if (username in connectionsCache && connectionsCache[username].isAuthorized) {
        return true;
      }
    }
  }

  // check for clients cache by platform
  const clientsCache = cache.clients[accountingPlatform];
  if (clientsCache?.length) {
    return true;
  }

  // @ts-ignore
  return clientSelectedGuard(route, state, true);
};
